/**
 * The offline write queue (brainstorm.md 1.6).
 *
 * School wifi drops. If a submission fails because of that, the mutation is
 * stashed in IndexedDB and retried when the connection comes back. Then the app
 * refetches and believes Canvas.
 *
 * ############################################################
 * THIS IS A QUEUE, NOT A SYNC ENGINE. Deliberately.
 *
 * It does not merge, reconcile, resolve conflicts, or hold any
 * authoritative state. Canvas always wins (brainstorm.md 1.1), so
 * the only job here is "try this again later, then refetch". The
 * moment this file starts growing a conflict-resolution strategy,
 * something has gone wrong with the design.
 * ############################################################
 *
 * Two rules keep it honest:
 *
 *  1. **Only network failures are queued.** A 401, a 403, or a validation
 *     error will fail identically on retry, so those surface to the user
 *     immediately instead of sitting in a queue pretending they might work.
 *  2. **Every retry goes through the same client**, so the live-write guard in
 *     `client.ts` applies to a replayed write exactly as it does to a fresh
 *     one. A queued mutation cannot be a back door around it.
 */
import { openDB, type IDBPDatabase } from 'idb';

import { CanvasHttpError, CanvasNetworkError, LiveWriteBlockedError } from './canvas/errors';
import {
  postSubmissionComment,
  setPlannerOverride,
  submitText,
  submitUrl,
} from './canvas/upload';

const DB_NAME = 'cgc-writes';
const DB_VERSION = 1;
const STORE = 'queue';

/**
 * What can be queued.
 *
 * File uploads are deliberately absent: a `File` cannot be serialized into
 * IndexedDB and replayed reliably, and silently re-uploading a file the student
 * may have since edited is worse than telling them the upload failed. File
 * submissions fail loudly and are retried by hand.
 */
export type QueuedWrite =
  | { kind: 'submitText'; courseId: string; assignmentId: string; body: string; comment?: string }
  | { kind: 'submitUrl'; courseId: string; assignmentId: string; url: string; comment?: string }
  | { kind: 'comment'; courseId: string; assignmentId: string; text: string }
  | { kind: 'plannerDone'; plannableType: string; plannableId: string; markedComplete: boolean };

export interface QueueEntry {
  id: string;
  write: QueuedWrite;
  /** Epoch ms. */
  queuedAt: number;
  /**
   * Monotonic tiebreaker. Two writes queued in the same millisecond share a
   * `queuedAt`, and replaying a comment before the submission it belongs to is
   * a real (if rare) way to confuse the student.
   */
  seq: number;
  attempts: number;
  lastError?: string;
}

/** Give up after this many tries so a permanently broken write cannot loop. */
const MAX_ATTEMPTS = 5;

let dbPromise: Promise<IDBPDatabase> | null = null;
let seqCounter = 0;

function db(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(STORE)) {
        database.createObjectStore(STORE, { keyPath: 'id' });
      }
    },
  });
  return dbPromise;
}

function newId(): string {
  // Not security-sensitive; just needs to be unique within one device.
  return `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Should this failure be retried later, or shown to the user now?
 *
 * Only transport failures and 5xx/429 qualify. Anything the server rejected on
 * its merits will be rejected identically next time.
 */
export function isRetryable(error: unknown): boolean {
  // The guard firing is never retryable — it is a refusal, not a failure.
  if (error instanceof LiveWriteBlockedError) return false;
  if (error instanceof CanvasNetworkError) return true;
  if (error instanceof CanvasHttpError) return error.isRetryable;
  return false;
}

export async function enqueue(write: QueuedWrite, error?: unknown): Promise<QueueEntry> {
  const entry: QueueEntry = {
    id: newId(),
    write,
    queuedAt: Date.now(),
    seq: (seqCounter += 1),
    attempts: 0,
    ...(error instanceof Error ? { lastError: error.message } : {}),
  };
  await (await db()).put(STORE, entry);
  return entry;
}

export async function listQueue(): Promise<QueueEntry[]> {
  try {
    const all = (await (await db()).getAll(STORE)) as QueueEntry[];
    return all.sort((a, b) => a.queuedAt - b.queuedAt || (a.seq ?? 0) - (b.seq ?? 0));
  } catch {
    return [];
  }
}

export async function removeFromQueue(id: string): Promise<void> {
  try {
    await (await db()).delete(STORE, id);
  } catch {
    /* already gone */
  }
}

export async function clearQueue(): Promise<void> {
  try {
    await (await db()).clear(STORE);
  } catch {
    /* nothing to clear */
  }
}

/** Replay one entry through the normal client, so the guard still applies. */
async function replay(write: QueuedWrite): Promise<void> {
  switch (write.kind) {
    case 'submitText':
      await submitText({
        courseId: write.courseId,
        assignmentId: write.assignmentId,
        body: write.body,
        ...(write.comment ? { comment: write.comment } : {}),
      });
      return;
    case 'submitUrl':
      await submitUrl({
        courseId: write.courseId,
        assignmentId: write.assignmentId,
        url: write.url,
        ...(write.comment ? { comment: write.comment } : {}),
      });
      return;
    case 'comment':
      await postSubmissionComment(write.courseId, write.assignmentId, write.text);
      return;
    case 'plannerDone':
      await setPlannerOverride(write.plannableType, write.plannableId, write.markedComplete);
      return;
  }
}

export interface FlushResult {
  succeeded: number;
  failed: number;
  abandoned: number;
}

/**
 * Try every queued write once.
 *
 * Entries are processed oldest first and sequentially — a burst of parallel
 * retries on a connection that has just come back is how you get rate limited
 * at the worst possible moment.
 */
export async function flushQueue(): Promise<FlushResult> {
  const entries = await listQueue();
  const result: FlushResult = { succeeded: 0, failed: 0, abandoned: 0 };

  for (const entry of entries) {
    try {
      await replay(entry.write);
      await removeFromQueue(entry.id);
      result.succeeded += 1;
    } catch (error) {
      const attempts = entry.attempts + 1;
      const message = error instanceof Error ? error.message : String(error);

      if (!isRetryable(error) || attempts >= MAX_ATTEMPTS) {
        // Stop pretending. The user needs to know this write is not going to
        // happen on its own — a silently abandoned homework submission is the
        // worst outcome in this app.
        await removeFromQueue(entry.id);
        result.abandoned += 1;
        console.error('[C-GC] Abandoning queued write after failure:', entry.write.kind, message);
      } else {
        await (await db()).put(STORE, { ...entry, attempts, lastError: message } satisfies QueueEntry);
        result.failed += 1;
      }
    }
  }

  return result;
}

/**
 * Flush whenever the browser says the connection is back, and once at startup.
 * Returns an unsubscribe function.
 */
export function startWriteQueue(onFlush?: (result: FlushResult) => void): () => void {
  let running = false;

  const run = () => {
    if (running) return;
    running = true;
    void flushQueue()
      .then((result) => {
        if (result.succeeded || result.abandoned) onFlush?.(result);
      })
      .catch(() => undefined)
      .finally(() => {
        running = false;
      });
  };

  window.addEventListener('online', run);
  // Coming back to the tab is the other moment connectivity typically returns.
  const onVisible = () => {
    if (document.visibilityState === 'visible' && navigator.onLine) run();
  };
  document.addEventListener('visibilitychange', onVisible);

  if (navigator.onLine) run();

  return () => {
    window.removeEventListener('online', run);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
